/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  QueryObserverOptions,
  QueryFunctionContext,
} from 'react-query/core';
import type TokenizedApi from '../TokenizedApi';

type QueryKeyFromArgs<TQueryArgs extends unknown[]> = [
  'tokenized',
  string | null,
  string,
  string,
  ...TQueryArgs,
];

type TokenizedQueryFunction<TQueryFnData, TQueryArgs extends unknown[]> = (
  context: QueryFunctionContext<QueryKeyFromArgs<TQueryArgs>>,
  ...queryArgs: TQueryArgs
) => Promise<TQueryFnData>;

type TokenizedQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryData,
  TQueryArgs extends unknown[],
> =
  | QueryObserverOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      QueryKeyFromArgs<TQueryArgs>
    >
  | ((
      options: any,
      ...queryArgs: TQueryArgs
    ) => QueryObserverOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      QueryKeyFromArgs<TQueryArgs>
    >);

function mergeQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryData,
  TQueryArgs extends unknown[] = unknown[],
>(
  queryArgs: TQueryArgs,
  ...optionsList: TokenizedQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryData,
    TQueryArgs
  >[]
): QueryObserverOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryData,
  QueryKeyFromArgs<TQueryArgs>
> {
  return optionsList.reduce(
    (options: any, overrideOptions) =>
      typeof overrideOptions === 'function'
        ? overrideOptions(options, ...queryArgs)
        : { ...options, ...overrideOptions },
    {},
  );
}

// For query functions (which are initially called 'query') ensures that the
// function has the same name as the property of the object which contains it
// or defaults to a unique random string if unable to identify the property
// eslint-disable-next-line @typescript-eslint/ban-types
function renameQueryFunction(object: Object, method: Function) {
  if (method.name === 'query') {
    const name =
      Object.entries(object).find(([, value]) => value === method)?.[0] ||
      `query-${Math.random().toString(36).slice(2)}`;
    Object.defineProperty(method, 'name', { value: name });
  }
}

/** @internal */
export default class Service extends EventTarget {
  /** @internal */
  tokenizedApi: TokenizedApi;

  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super();
    this.tokenizedApi = tokenizedApi;
  }

  /** @internal */
  getIntl() {
    return this.tokenizedApi.intl.getIntl();
  }

  /** @internal */
  getLocale() {
    return this.tokenizedApi.intl.getLocale();
  }

  /** @internal */
  makeCacheKey<
    TQueryFnData,
    TError,
    TData,
    TQueryData,
    TQueryArgs extends unknown[],
  >(
    method: (
      ...queryArgs: TQueryArgs
    ) => QueryObserverOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      QueryKeyFromArgs<TQueryArgs>
    >,
    ...args: TQueryArgs
  ): QueryKeyFromArgs<TQueryArgs> {
    renameQueryFunction(this, method);
    return [
      'tokenized',
      this.tokenizedApi.sessionId,
      this.constructor.name,
      method.name,
      ...args,
    ];
  }

  /** @internal */
  makeQuery<
    TQueryFnData,
    TError,
    TData = TQueryFnData,
    TQueryData = TQueryFnData,
    TQueryArgs extends unknown[] = unknown[],
  >(
    options:
      | QueryObserverOptions<
          TQueryFnData,
          TError,
          TData,
          TQueryData,
          QueryKeyFromArgs<TQueryArgs>
        >
      | ((
          options: any,
          ...queryArgs: TQueryArgs
        ) => QueryObserverOptions<
          TQueryFnData,
          TError,
          TData,
          TQueryData,
          QueryKeyFromArgs<TQueryArgs>
        >),
    implementation: TokenizedQueryFunction<TQueryFnData, TQueryArgs>,
  ) {
    const client = this.tokenizedApi.getQueryClient();

    /** Create a query options object using the arguments specified */
    const query = (
      ...queryArgs: TQueryArgs
    ): QueryObserverOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryData,
      QueryKeyFromArgs<TQueryArgs>
    > =>
      mergeQueryOptions(
        queryArgs,
        {
          queryKey: this.makeCacheKey(query, ...queryArgs),
          queryFn: (context) => implementation(context, ...queryArgs),
          enabled: this.tokenizedApi.account.getEnableAllQueries(),
        },
        options,
      );

    // TODO?: The types here aren’t correct in every case, since
    // some override options (like a selector) should change the
    // type (different TData type). Not clear how to get TypeScript
    // to work this out though
    /** @internal */
    query.withOptions = (
      overrideOptions: QueryObserverOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryData,
        QueryKeyFromArgs<TQueryArgs>
      > = {},
      ...queryArgs: TQueryArgs
    ) => mergeQueryOptions(queryArgs, query(...queryArgs), overrideOptions);

    /** @internal */
    query.makeCacheKey = (...args: TQueryArgs) =>
      this.makeCacheKey(query, ...args);

    /**
     * Fetch the results of the query with the arguments specified.
     * If there is non-stale data in the query cache, that will be returned
     * immediately without contacting the server.
     */
    query.fetch = async (...args: TQueryArgs) => {
      return await client.fetchQuery(query(...args));
    };

    query.fetchWithOptions = async (
      overrideOptions: QueryObserverOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryData,
        QueryKeyFromArgs<TQueryArgs>
      >,
      ...args: TQueryArgs
    ) => {
      return await client.fetchQuery(
        query.withOptions(overrideOptions, ...args),
      );
    };

    query.invalidate = (...args: TQueryArgs) => {
      const cacheKey = this.makeCacheKey(query, ...args);
      return client.invalidateQueries(cacheKey, undefined, {
        cancelRefetch: true,
      }); // This is async
    };

    query.reset = (...args: TQueryArgs) => {
      return client.resetQueries(this.makeCacheKey(query, ...args));
    };

    query.isDataAvailable = (...args: TQueryArgs) => {
      const cacheKey = this.makeCacheKey(query, ...args);
      return client.getQueryState(cacheKey)?.status === 'success';
    };

    query.getQueryData = (...args: TQueryArgs): TData | undefined => {
      return client.getQueryData(this.makeCacheKey(query, ...args));
    };

    /** @internal */
    query.setQueryData = (
      updater: TData | ((input: TData | undefined) => TData),
      ...args: TQueryArgs
    ) => {
      return client.setQueryData(this.makeCacheKey(query, ...args), updater);
    };

    return query;
  }
}

export function seconds(count: number) {
  return count * 1000;
}

export function minutes(count: number) {
  return seconds(count * 60);
}

export function hours(count: number) {
  return minutes(count * 60);
}
